import React, { useState, useEffect } from 'react';
import T from '../../../components/common/T';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../../api/axiosConfig';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { 
  ShieldCheckIcon, 
  ArrowLeftIcon, 
  CheckCircleIcon, 
  XCircleIcon 
} from '@heroicons/react/24/outline';
import { useAuth } from '../../../context/AuthContext';
import RoleMatrix from '../../../components/rbac/RoleMatrix'; // 🟢 Matrix Component

const EditRole = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { checkAccess } = useAuth();

  // State
  const [roleName, setRoleName] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState("Active");
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // 1. Guard Check
  useEffect(() => {
    if (!checkAccess('role.edit')) {
        toast.error("Access Denied");
        navigate('/admin/roles');
    }
  }, [checkAccess, navigate]);

  // 2. Fetch Role Data (The Fix)
  useEffect(() => {
    const fetchRole = async () => {
        try {
            // 🟢 Fetch directly from API by ID
            const res = await api.get(`/roles/${id}`);
            
            // Populate State
            setRoleName(res.data.name);
            setDescription(res.data.description || "");
            setStatus(res.data.status || "Active");
            // Ensure permissions is always an array
            setSelectedPermissions(res.data.permissions || []);
        } catch (err) {
            console.error(err);
            toast.error("Failed to load role. It might not exist.");
            // Only redirect if there is a genuine error (like 404)
            setTimeout(() => navigate('/admin/roles'), 2000);
        } finally {
            setLoading(false);
        }
    };

    if (checkAccess('role.edit')) {
        fetchRole();
    }
  }, [id, navigate, checkAccess]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!roleName.trim()) return toast.warn("Role Name cannot be empty");

    setSaving(true);
    try {
      // 🟢 API Update
      await api.put(`/roles/${id}`, {
        name: roleName,
        description: description,
        status: status, // Send status update
        permissions: selectedPermissions 
      });

      toast.success("Role updated successfully!");
      setTimeout(() => navigate('/admin/roles'), 1000);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Update failed");
      setSaving(false);
    }
  };

  if (loading) {
      return (
          <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
              <p className="mt-4 text-gray-500 font-medium"><T>Loading Role Configuration...</T></p>
          </div>
      );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen font-sans">
      
      {/* Header Area */}
      <div className="max-w-6xl mx-auto mb-8">
        <button 
            onClick={() => navigate('/admin/roles')}
            className="flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-4 transition-colors"
        >
            <ArrowLeftIcon className="w-4 h-4" /> Back to List
        </button>

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
                <div className="p-3 bg-white border border-gray-200 rounded-xl shadow-sm">
                    <ShieldCheckIcon className="w-8 h-8 text-amber-500" />
                </div>
                <div>
                    <h2 className="text-base font-bold text-gray-900"><T>Edit Role Configuration</T></h2>
                    <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                        <span><T>Role ID: </T><span className="font-mono font-bold text-gray-700">#{id}</span></span>
                        <span>•</span>
                        <span>{selectedPermissions.length} Permissions Active</span>
                    </div>
                </div>
            </div>
            
            {/* Status Badge */}
            <div className={`px-4 py-2 rounded-lg border flex items-center gap-2 font-bold text-sm ${
                status === 'Active' 
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                : 'bg-gray-100 text-gray-600 border-gray-200'
            }`}>
                {status === 'Active' ? <CheckCircleIcon className="w-5 h-5"/> : <XCircleIcon className="w-5 h-5"/>}
                {status} Status
            </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto">
        <form onSubmit={handleSubmit} className="space-y-8">
          
          {/* 1. Details Card */}
          <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-200">
            <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-100">
                <h3 className="text-base font-bold text-gray-800"><T>Role Information</T></h3>
                
                {/* Status Toggle Switch */}
                <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-600"><T>Role Status:</T></span>
                    <button
                        type="button"
                        onClick={() => setStatus(prev => prev === 'Active' ? 'Inactive' : 'Active')}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 ${
                            status === 'Active' ? 'bg-emerald-500' : 'bg-gray-300'
                        }`}
                    >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            status === 'Active' ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <label className="block text-gray-700 text-xs font-bold mb-2 uppercase tracking-wider"><T>Role Name</T></label>
                <input 
                  type="text" 
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 transition-all font-medium text-gray-900"
                  value={roleName}
                  onChange={(e) => setRoleName(e.target.value)}
                  placeholder="e.g. Project Manager"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-xs font-bold mb-2 uppercase tracking-wider"><T>Description</T></label>
                <input 
                  type="text" 
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 transition-all text-gray-700"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe the role's responsibilities..."
                />
              </div>
            </div>
          </div>

          {/* 2. Toggle Matrix */}
          <div>
            <div className="flex justify-between items-end mb-4 px-1">
              <h3 className="text-base font-bold text-gray-800"><T>Permission Control</T></h3>
              <p className="text-sm text-gray-500"><T>Toggle the modules this role can access.</T></p>
            </div>
            
            <RoleMatrix 
              selectedPermissions={selectedPermissions}
              setSelectedPermissions={setSelectedPermissions}
            />
          </div>

          {/* 3. Actions */}
          <div className="flex justify-end gap-4 pt-8 border-t border-gray-200">
            <button 
              type="button"
              onClick={() => navigate('/admin/roles')}
              className="px-6 py-3 bg-white border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 hover:text-gray-900 transition-all"
            >
              Cancel Changes
            </button>
            <button 
              type="submit"
              disabled={saving}
              className="px-8 py-3 bg-amber-600 text-white font-bold rounded-lg hover:bg-amber-700 shadow-lg hover:shadow-amber-500/30 transition-all transform hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Save Configuration'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditRole;